import type { InventoryKgItem } from '@toggle/toggle';

/** Subsection model used only by `KnowledgeGraphInventoryContent`. */
export type ResolvedKgInventoryRow =
  | { kind: 'theme-company'; themeId: string }
  | { kind: 'company-theme'; entityTag: string }
  | { kind: 'company-brand'; entityTag: string }
  | {
      kind: 'company-competitor';
      entityTag: string;
      themeFilterId?: string;
    }
  | { kind: 'company-geography'; entityTag: string };

const resolveKgRow = (item: InventoryKgItem): ResolvedKgInventoryRow | null => {
  const key = item.node_key.trim();

  if (item.node_type === 'company') {
    switch (item.relation_type) {
      case 'competitor': {
        const themeFilterId = item.filter?.find(
          f => f.node_type === 'theme'
        )?.node_key;
        return {
          kind: 'company-competitor',
          entityTag: key,
          themeFilterId: themeFilterId?.trim() || undefined,
        };
      }

      case 'theme':
        return { kind: 'company-theme', entityTag: key };
      case 'product':
        return { kind: 'company-brand', entityTag: key };
      case 'country':
      case 'region':
        return { kind: 'company-geography', entityTag: key };
      default:
        return null;
    }
  }

  if (
    item.node_type === 'theme' &&
    (item.relation_type === 'theme' || item.relation_type === 'company')
  ) {
    return { kind: 'theme-company', themeId: key };
  }

  return null;
};

const dedupeKey = (row: ResolvedKgInventoryRow): string => {
  switch (row.kind) {
    case 'theme-company':
      return `theme-company:${row.themeId}`;
    case 'company-theme':
      return `company-theme:${row.entityTag}`;
    case 'company-brand':
      return `company-brand:${row.entityTag}`;
    case 'company-competitor':
      return `company-competitor:${row.entityTag}:${row.themeFilterId ?? ''}`;
    case 'company-geography':
      return `company-geography:${row.entityTag}`;
  }
};

/**
 * Maps API `kg.items` into rows for the inventory UI.
 * Collapses duplicate geography subsections for the same company (e.g. separate country + region lines).
 */
export const resolveKgInventoryRows = (
  items: InventoryKgItem[]
): ResolvedKgInventoryRow[] => {
  const seen = new Set<string>();
  const out: ResolvedKgInventoryRow[] = [];

  for (const raw of items) {
    const row = resolveKgRow(raw);

    if (!row) {
      continue;
    }

    const key = dedupeKey(row);

    if (seen.has(key)) {
      continue;
    }

    seen.add(key);
    out.push(row);
  }

  return out;
};
